import { AxiosError } from 'axios'
import { IUserAuthenticatedRequest, IUserAuthenticatedResponse, TMfaMethodType } from 'dtos/User'
import { confirmEnrollment, resendLoginChallenge, startEnrollment, verifyLoginChallenge } from 'usecases/mfa'
import { authenticateWithChallenge } from 'usecases/user'
import { defaultsHeadersAxios } from 'services/api'
import { useRef, useState } from 'react'

type TFlowStep =
  | { kind: 'idle' }
  | { kind: 'method_required'; methods: TMfaMethodType[] }
  | { kind: 'challenge'; challengeId: string; method: TMfaMethodType; maskedTarget: string | null }
  | { kind: 'enrollment_select' }
  | {
      kind: 'enrollment_confirm'
      method: TMfaMethodType
      enrollmentId: string
      maskedTarget: string | null
      secret?: string
      otpauthUri?: string
    }
  | { kind: 'recovery_codes'; codes: string[] }

// CA6: um único ponto lança para credenciais inválidas — todo chamador
// (Login, Registration/paciente, Registration/profissionais) já tem um
// try/catch com a mensagem genérica de hoje ("E-mail ou senha incorretos"),
// então a distinção entre e-mail inexistente e senha errada nunca chega a
// eles.
export class InvalidCredentialsError extends Error {}

const apiErrorCode = (error: unknown): string | undefined =>
  (error as AxiosError<{ code?: string }>).response?.data?.code

// Orquestra o fluxo de login com 2FA (nota 3 §1/§2/§3 da feature
// portal-cannect-2fa) para os pontos de entrada de login PADRÃO (não
// checkout — esses mandam login_context:'checkout' e nunca entram aqui,
// porque a resposta nesse caso é sempre a (a)/(d), idêntica a hoje).
// `onAuthenticated` é chamado só quando uma sessão completa é emitida —
// mesmo callback que cada página já usa hoje (setTokenApi, setUser,
// getRoles, redirecionamento).
export const useTwoFactorLoginFlow = (onAuthenticated: (data: IUserAuthenticatedResponse) => void) => {
  const [step, setStep] = useState<TFlowStep>({ kind: 'idle' })
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const credentialsRef = useRef<{ email: string; password: string } | null>(null)
  const extraRef = useRef<Partial<IUserAuthenticatedRequest>>({})

  const reset = () => {
    setStep({ kind: 'idle' })
    setErrorMessage('')
  }

  const relogin = async () => {
    if (!credentialsRef.current) return
    try {
      const result = await authenticateWithChallenge({ ...credentialsRef.current, ...extraRef.current })
      if (result.kind === 'authenticated') {
        reset()
        onAuthenticated(result.data)
        return
      }
      if (result.kind === 'challenge') {
        setStep({
          kind: 'challenge',
          challengeId: result.data.challenge_id,
          method: result.data.method,
          maskedTarget: result.data.masked_target
        })
        return
      }
      // method_required/enrollment_required não deveriam ocorrer aqui — o
      // método recém-confirmado já é o único ativo (ou o escolhido antes).
      setErrorMessage('Não foi possível concluir o login. Tente novamente.')
    } catch {
      setErrorMessage('Não foi possível concluir o login. Tente novamente.')
    }
  }

  const login = async (email: string, password: string, extra: Partial<IUserAuthenticatedRequest> = {}) => {
    credentialsRef.current = { email, password }
    extraRef.current = extra
    setErrorMessage('')
    setLoading(true)
    try {
      const result = await authenticateWithChallenge({ email, password, ...extra })

      if (result.kind === 'authenticated') {
        reset()
        onAuthenticated(result.data)
        return
      }
      if (result.kind === 'challenge') {
        setStep({
          kind: 'challenge',
          challengeId: result.data.challenge_id,
          method: result.data.method,
          maskedTarget: result.data.masked_target
        })
        return
      }
      if (result.kind === 'method_required') {
        setStep({ kind: 'method_required', methods: result.methods })
        return
      }
      if (result.kind === 'enrollment_required') {
        defaultsHeadersAxios(result.token)
        setStep({ kind: 'enrollment_select' })
        return
      }
      throw new InvalidCredentialsError()
    } finally {
      setLoading(false)
    }
  }

  const chooseMethod = async (method: TMfaMethodType) => {
    if (!credentialsRef.current) return
    setLoading(true)
    setErrorMessage('')
    try {
      await login(credentialsRef.current.email, credentialsRef.current.password, { ...extraRef.current, method })
    } finally {
      setLoading(false)
    }
  }

  const submitCode = async (code: string) => {
    if (step.kind !== 'challenge') return
    setLoading(true)
    setErrorMessage('')
    try {
      const result = await verifyLoginChallenge({ challenge_id: step.challengeId, code })
      if (result.kind === 'authenticated') {
        reset()
        onAuthenticated(result.data)
      }
    } catch (error) {
      const errorCode = apiErrorCode(error)
      if (errorCode === 'CHALLENGE_LOCKED') {
        setErrorMessage('Muitas tentativas. Solicite um novo código.')
      } else if (errorCode === 'CHALLENGE_NOT_FOUND') {
        setErrorMessage('Código expirado. Solicite um novo código.')
      } else {
        setErrorMessage('Código inválido ou expirado.')
      }
    } finally {
      setLoading(false)
    }
  }

  // CA12 — recovery code é cross-canal: o mesmo lote gerado no manager-cannect
  // vale aqui. Senha reaproveitada do login (D17: recuperação exige os dois
  // fatores — código + senha).
  const submitRecoveryCode = async (recoveryCode: string) => {
    if (step.kind !== 'challenge' || !credentialsRef.current) return
    setLoading(true)
    setErrorMessage('')
    try {
      const result = await verifyLoginChallenge({
        challenge_id: step.challengeId,
        recovery_code: recoveryCode,
        password: credentialsRef.current.password
      })
      if (result.kind === 'authenticated') {
        reset()
        onAuthenticated(result.data)
        return
      }
      // D18: recovery_code nunca loga direto — só destrava um token de
      // escopo restrito para reconfigurar o 2FA (reseta todos os métodos).
      defaultsHeadersAxios(result.recoveryToken)
      setStep({ kind: 'enrollment_select' })
    } catch (error) {
      const errorCode = apiErrorCode(error)
      setErrorMessage(
        errorCode === 'PASSWORD_REQUIRED' ? 'Senha incorreta.' : 'Código de recuperação inválido ou já usado.'
      )
    } finally {
      setLoading(false)
    }
  }

  const resendCode = async () => {
    if (step.kind !== 'challenge') return
    setLoading(true)
    setErrorMessage('')
    try {
      const data = await resendLoginChallenge(step.challengeId)
      setStep({
        kind: 'challenge',
        challengeId: data.challenge_id,
        method: data.method,
        maskedTarget: data.masked_target
      })
    } catch {
      setErrorMessage('Não foi possível reenviar o código. Tente novamente em instantes.')
    } finally {
      setLoading(false)
    }
  }

  const chooseEnrollMethod = async (method: TMfaMethodType) => {
    if (step.kind !== 'enrollment_select') return
    setLoading(true)
    setErrorMessage('')
    try {
      const data = await startEnrollment(method, undefined, { skipAuthRedirect: true })
      setStep(
        'secret' in data
          ? {
              kind: 'enrollment_confirm',
              method,
              enrollmentId: data.enrollment_id,
              maskedTarget: null,
              secret: data.secret,
              otpauthUri: data.otpauth_uri
            }
          : { kind: 'enrollment_confirm', method, enrollmentId: data.enrollment_id, maskedTarget: data.masked_target }
      )
    } catch (error) {
      const errorCode = apiErrorCode(error)
      setErrorMessage(
        errorCode === 'PHONE_UNAVAILABLE'
          ? 'Cadastre um celular válido antes de ativar o SMS.'
          : 'Não foi possível iniciar a configuração deste método.'
      )
    } finally {
      setLoading(false)
    }
  }

  const resendEnrollCode = async () => {
    if (step.kind !== 'enrollment_confirm' || step.method === 'totp') return
    await chooseEnrollMethod(step.method)
  }

  const confirmEnrollCode = async (code: string) => {
    if (step.kind !== 'enrollment_confirm') return
    setLoading(true)
    setErrorMessage('')
    try {
      const data = await confirmEnrollment(step.method, step.enrollmentId, code, { skipAuthRedirect: true })
      if (data.recovery_codes) {
        setStep({ kind: 'recovery_codes', codes: data.recovery_codes })
      } else {
        await relogin()
      }
    } catch {
      setErrorMessage('Código inválido ou expirado.')
    } finally {
      setLoading(false)
    }
  }

  const finishAfterRecoveryCodes = async () => {
    setLoading(true)
    try {
      await relogin()
    } finally {
      setLoading(false)
    }
  }

  return {
    step,
    loading,
    errorMessage,
    login,
    chooseMethod,
    submitCode,
    submitRecoveryCode,
    resendCode,
    chooseEnrollMethod,
    resendEnrollCode,
    confirmEnrollCode,
    finishAfterRecoveryCodes,
    reset
  }
}
